import { motion } from "framer-motion";
import { useRouter } from "next/router";
import Head from "next/head";
import Custom404 from "../404";
import Container from "../../components/container";
import PostBody from "../../components/post-body";
import MoreStories from "../../components/more-stories";
import Header from "../../components/header";
import PostHeader from "../../components/post-header";
import SectionSeparator from "../../components/section-separator";
import Layout from "../../components/layout";
import { getAllPostsWithSlug, getPostAndMorePosts } from "../../lib/api";
import PostTitle from "../../components/post-title";

export default function Post({ post, postHtml, morePosts, preview, person }) {
  const router = useRouter();

  if (!router.isFallback && !post) {
    return <Custom404 />;
  }

  return (
    <Layout preview={preview}>
      <Container>
        {router.isFallback ? (
          <div
            role="status"
            className="flex flex-col min-h-screen animate-pulse pb-16"
          >
            <Head>
              <title>Loading…</title>
            </Head>

            <div className="h-screen">
              <div className="h-3/4 bg-slate-100 rounded-3xl dark:bg-slate-300 mb-8"></div>
              <div className="w-full max-w-4xl mx-auto">
                <PostTitle>Loading...</PostTitle>
                <div className="h-4 bg-slate-100 rounded-full dark:bg-slate-300 max-w-4xl my-8"></div>
                <div className="h-2 bg-slate-100 rounded-full dark:bg-slate-300 max-w-[360px] lg:max-w-[720px] mb-2.5"></div>
                <div className="h-2 bg-slate-100 rounded-full dark:bg-slate-300 mb-2.5"></div>
                <div className="h-2 bg-slate-100 rounded-full dark:bg-slate-300 max-w-[330px] lg:max-w-[660px] mb-2.5"></div>
                <div className="h-2 bg-slate-100 rounded-full dark:bg-slate-300 max-w-[300px] lg:max-w-[600px] mb-2.5"></div>
                <div className="h-2 bg-slate-100 rounded-full dark:bg-slate-300 max-w-[360px] lg:max-w-[720px]"></div>
                <span className="sr-only">Loading...</span>
              </div>
            </div>
          </div>
        ) : (
          <>
            <article className="post">
              <Head>
                <title>
                  {post && post.title
                    ? `${post.title}  | Salazarpardo Design Portfolio`
                    : `Salazarpardo Design Portfolio`}
                </title>
                <meta property="og:image" content={post.heroImage.url} />
              </Head>
              <PostHeader
                title={post.title}
                coverImage={post.heroImage}
                date={post.publishDate}
                author={post.author}
                tags={post.tags}
                description={post.description}
              />
              <PostBody content={postHtml} video={post.video} />
            </article>
            <SectionSeparator />
            {morePosts && morePosts.length > 0 && (
              <MoreStories posts={morePosts} />
            )}
            <SectionSeparator />
            <div className="banner mx-auto max-w-3xl pt-20 pb-32 sm:pt-48 sm:pb-40">
              <motion.div
                initial={{ opacity: 0, marginTop: 80 }}
                whileInView={{ opacity: 1, marginTop: 0 }}
                viewport={{ once: true }}
              >
                <h3 className="text-2xl text-left">{person.contact}</h3>
                <p className="text-left">
                  <a
                    href="https://calendly.com/salazarpardo/intro"
                    target="_blank"
                    title="Calendly link"
                  >
                    Book a call
                  </a>
                </p>
              </motion.div>
            </div>
          </>
        )}
      </Container>
    </Layout>
  );
}

export async function getStaticProps({ params, preview = false }) {
  const data = await getPostAndMorePosts(params.slug, preview);

  return {
    props: {
      preview,
      post: data?.post ?? null,
      postHtml: data?.postHtml ?? null,
      morePosts: data?.morePosts ?? null,
      person: data?.person ?? null,
    },
    revalidate: 60,
  };
}

export async function getStaticPaths() {
  const allPosts = await getAllPostsWithSlug();
  return {
    paths: allPosts?.map(({ slug }) => `/posts/${slug}`) ?? [],
    fallback: true,
  };
}
